import { kea } from 'kea'
import PropTypes from 'prop-types'
import Bugsnag from '~/scenes/bugsnag'

import sceneLogic from '~/scenes/search/logic'
import filtersLogic from '~/scenes/search/filters-page/logic'

import controller from '~/scenes/search/controller.rb'
import { coordsEqual } from '~/scenes/search/search-page/map/utils'
import geoViewport from '@mapbox/geo-viewport'

export default kea({
  path: () => ['scenes', 'search', 'map'],

  connect: {
    props: [sceneLogic, ['filter', 'isFullScreenMap']],
    actions: [
      sceneLogic,
      ['updateFilter', 'setNewFilter', 'receiveResults'],
      filtersLogic,
      ['openFilter', 'applyFilter']
    ]
  },

  actions: () => ({
    updateMapCenterZoom: (center, zoom) => ({ center, zoom }),
    // bounds = { ne: [lat, lng], sw: [lat, lng] }
    updateMapPosition: ({ center, zoom, bounds, width, height }) => ({
      center,
      zoom: Math.round(zoom),
      bounds,
      width,
      height
    }),
    performMapSearch: true,
    closeMap: true,
    setTemporaryResults: results => ({ results })
  }),

  reducers: ({ actions }) => ({
    temporaryMapPosition: [
      null,
      PropTypes.object,
      {
        [actions.updateMapPosition]: (_, payload) => payload,
        [actions.closeMap]: () => null,
        [actions.receiveResults]: (state, payload) => (payload.meta.fromHistory ? null : state),
        [actions.updateFilter]: (state, payload) => (payload.filterChanges.fromLocationAutocomplete ? null : state)
      }
    ],
    temporaryResults: [
      null,
      PropTypes.object,
      {
        [actions.setTemporaryResults]: (_, payload) => payload.results,
        [actions.closeMap]: () => null,
        [actions.receiveResults]: (state, payload) => (payload.meta.fromHistory ? null : state),
        [actions.applyFilter]: () => null
      }
    ]
  }),

  selectors: ({ selectors }) => ({
    filterMapPosition: [
      () => [selectors.filter],
      ({ bounds, center, zoom, width, height }) => ({ bounds, center, zoom, width, height }),
      PropTypes.object
    ],

    // this is passed to pigeon-maps
    mapPosition: [
      () => [selectors.temporaryMapPosition, selectors.filterMapPosition],
      (temporaryMapPosition, filterMapPosition) => temporaryMapPosition || filterMapPosition,
      PropTypes.object
    ]
  }),

  listeners: ({ actions, values }) => ({
    [actions.updateMapCenterZoom]: function ({ center, zoom }) {
      const { updateMapPosition } = actions

      let width = 400
      let height = 490

      if (typeof window !== 'undefined') {
        const mapDiv = window.document.getElementById('search-page-main-pigeon-map')
        if (mapDiv && mapDiv.clientWidth > 0 && mapDiv.clientHeight > 0) {
          width = mapDiv.clientWidth
          height = mapDiv.clientHeight
        }
      }

      const rawBounds = geoViewport.bounds(center, zoom, [width, height])
      const bounds = {
        ne: [rawBounds[2], rawBounds[3]],
        sw: [rawBounds[0], rawBounds[1]]
      }

      updateMapPosition({ center, zoom, width, height, bounds })
    },

    [actions.updateMapPosition]: async function ({ bounds, center, zoom, width, height }, breakpoint) {
      const { setTemporaryResults, performMapSearch } = actions
      const { filter } = values

      if (!values.isFullScreenMap) {
        await breakpoint(10)
      }

      if (coordsEqual(filter.center, center) && filter.zoom === zoom) {
        return
      }

      if (!values.isFullScreenMap) {
        performMapSearch()
        return
      }

      const newFilter = {
        ...filter,
        bounds: [bounds.ne, bounds.sw],
        center: center,
        zoom: zoom,
        page: 1,
        width: width,
        height: height,
        only_coordinates: true
      }

      try {
        const response = await controller.index(newFilter)
        setTemporaryResults(response.results)
      } catch (e) {
        Bugsnag.notify(e)
      }
    },

    [actions.performMapSearch]: function () {
      const { updateFilter } = actions
      const { temporaryMapPosition, filter } = values

      if (!temporaryMapPosition) {
        return
      }

      const { center, zoom, bounds, width, height } = temporaryMapPosition

      updateFilter({
        coords_from_location: false,
        bounds: [bounds.ne, bounds.sw],
        center: center,
        zoom: zoom,
        page: 1,
        width: width,
        height: height,
        from_map: true,
        filters: (filter.filters || '')
          .split(',')
          .filter(a => a !== 'map')
          .join(',')
      })
    },

    [actions.closeMap]: function () {
      const { openFilter } = actions
      openFilter('')
    }
  })
})
